import { constants } from "../constant/constants";
import { HttpHelper } from "../helper/http-helper-service";
import { TicketPaginationValues } from "../model/ticket/ticket-pagination-values";

import PrototypeInstance from "../helper/prototype-instance";
import {
  applyClientPortalUnreadStateOnTicketSync,
  type TicketRecentCommentSnapshot,
} from "../entity-service/ticket-entity-service";
import { ticketEntity } from "../model/entity/ticket-entity";
import { isClosedTicketOutsideListWindow } from "../helper/ticket-closed-list-window";

export default class ticketService {
  static GetAllTickets(pagination: TicketPaginationValues) {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.getAllTickets,
      pagination,
    );
  }

  static getTicketDetailByIdForSignalR(...params: any[]): Promise<any> {
    if (params[0] != null && params[1] != null) {
      return HttpHelper.getData(
        constants.apiUrl.ticketUrls.getTicketDetailById +
          "?TicketId=" +
          params[0] +
          "&signalRMessageTick=" +
          params[1],
      );
    } else {
      return HttpHelper.getData(
        constants.apiUrl.ticketUrls.getTicketDetailById +
          "?TicketId=" +
          params[0].ticketId +
          "&signalRMessageTick=" +
          params[0].tick,
      );
    }
  }

  // Contact-scoped read (`comment/cx/filter/list`). The technician `comment/list/{id}` route
  // checks nothing but the tenant, so it hands a contact other threads' comments.
  static getTicketComments = (
    id: string,
    params: object,
    pageNumber: number,
    pageSize: number,
  ) => {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.getCommentListByFilter,
      {
        ...params,
        ticketId: id,
        pageNumber,
        pageSize,
        isInternal: false,
        types: ["Comment", "ThirdPartyConversation", "ApprovalComment", "Event"],
      },
    );
  };

  // Contact-scoped point read (`comment/cx`). The technician `comment/{id}/{ticketId}` route
  // returns any comment in the tenant, internal notes included. The server answers 404 for a
  // comment this contact may not read, which is surfaced as null.
  static getCommentByCommentId = async (commentId: string, ticketId: string) => {
    try {
      return await HttpHelper.postData(
        constants.apiUrl.ticketUrls.getCommentByIdForCx,
        { commentId, ticketId },
      );
    } catch {
      return null;
    }
  };

  // Contact-scoped delta (`comment/cx/tick/list`); the server takes the contact from the
  // request identity and forces IsInternal=false. Unwrapped to the bare comment array.
  static getCommentListByTick = async (object: any) => {
    const response = await HttpHelper.postData(
      constants.apiUrl.ticketUrls.getCommentListByTick,
      {
        pageNumber: 1,
        pageSize: 20,
        types: ["Comment", "ThirdPartyConversation", "ApprovalComment", "Event"],
        ...object,
      },
    );
    return response?.comments ?? [];
  };

  static async getTicketsByTick(object: any) {
    const result = await HttpHelper.postData(
      constants.apiUrl.ticketUrls.ticketbytick,
      object,
    );

    let tickets = result.tickets;
    if (!result.ticketsToDelete) {
      result.ticketsToDelete = [];
    }

    // The server already sends closed tickets past the reopen window in the remove-set; this keeps
    // every tick consumer in step with the SignalR `cx/detail` path should the two ever disagree.
    if (Array.isArray(tickets)) {
      const expired = tickets.filter(isClosedTicketOutsideListWindow);
      if (expired.length > 0) {
        tickets = tickets.filter((t: any) => !isClosedTicketOutsideListWindow(t));
        result.ticketsToDelete = result.ticketsToDelete.concat(expired);
      }
    }

    if (tickets != null) {
      const store = PrototypeInstance.globalProperties.$store;
      const selectedTicketId = store.getters[
        "ticketDetailModule/getTicketId"
      ] as string;
      const viewerContactId = store.getters[
        "ContactModule/getContactId"
      ] as number;
      const ticketToUpdate = tickets as any[];

      ticketToUpdate.forEach((ticket: any) => {
        const existing = ticketEntity.find(String(ticket.ticketId ?? ""));
        applyClientPortalUnreadStateOnTicketSync(
          ticket,
          selectedTicketId,
          existing?.isUnread,
          viewerContactId,
        );
      });

      return {
        tickets: ticketToUpdate,
        ticketsToDelete: result.ticketsToDelete.map(
          (ticket: any) => ticket.ticketId,
        ),
      };
    } else {
      return {
        tickets: tickets,
        ticketsToDelete: result.ticketsToDelete.map(
          (ticket: any) => ticket.ticketId,
        ),
      };
    }
  }

  static getTicketCosmosCommentById = (id: number, params: object) => {
    let url = constants.apiUrl.ticketUrls.getTicketCosmosCommentById + "/" + id;
    return HttpHelper.getData(url, params);
  };

  static saveTicketComment(dataModel: any) {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.saveTicketComment,
      dataModel,
    );
  }

  static createEncryptedTextLink = (params: object) => {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.createEncryptedTextLink,
      params,
    );
  };

  static getCommentListByFilter = (object: any) => {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.getCommentListByFilter,
      object,
    );
  };

  /** Latest public comment visible to the logged-in contact (technician or own contact posts). */
  static getRecentCommentForCx = (ticketId: string) => {
    return HttpHelper.getData(
      constants.apiUrl.ticketUrls.getRecentCommentForCx + "/" + ticketId,
    ) as Promise<TicketRecentCommentSnapshot | null>;
  };

  static updateTicketComment(dataModel: any) {
    return HttpHelper.patchData(
      constants.apiUrl.ticketUrls.updateTicketComment,
      dataModel,
    );
  }

  // No contact-scoped by-ids route exists, so fan out to `comment/cx` and drop what the
  // server refuses. `object` carries `ticketId` and `commentIds`.
  static getCommentsByCommentIds = async (object: any) => {
    const { ticketId, commentIds } = object ?? {};
    if (!ticketId || !Array.isArray(commentIds)) {
      return [];
    }
    const results = await Promise.all(
      commentIds.map((id: string) =>
        ticketService.getCommentByCommentId(id, ticketId),
      ),
    );
    return results.filter(Boolean);
  };

  static getBlobSharedAccessSignature = (params: object) => {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.getBlobSharedAccessSignature,
      params,
    );
  };

  static setCommentReaction = (params: object) => {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.setCommentReaction,
      params,
    );
  };

  static UpdateTicketContacts(object: any) {
    const url = constants.apiUrl.ticketUrls.updateticketcontact;
    return HttpHelper.patchData(url, object);
  }

  static setCommentCCEmail = (params: object) => {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.setCommentCCEmail,
      params,
    );
  };

  static showHideCommentById = (object: any) => {
    return HttpHelper.patchData(
      constants.apiUrl.ticketUrls.showHideCommentById,
      object,
    );
  };

  static async getServiceProviderAllTick() {
    return HttpHelper.getData(
      constants.apiUrl.ticketUrls.getServiceProviderTicketTick,
      null,
    );
  }

  static saveTicket(dataModel: any) {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.saveTicket,
      dataModel,
    );
  }

  /** `ticket/conversation/cx/{ticketId}` - the conversations on this ticket the logged-in contact is
   * party to (public thread, side conversations, approvals), in display order. The public row is
   * omitted when the contact is not on the ticket's To/CC. */
  static getConversationTabs(ticketId: string) {
    return HttpHelper.getData(
      constants.apiUrl.ticketUrls.getConversationTabs + "/" + ticketId,
    );
  }

  /** `ticket/approval/cx/respond` - records the logged-in contact's own answer to an approval. The
   * approver row is resolved server-side from the caller's identity. */
  static respondToApproval(object: {
    ticketId: string;
    approvalId: string;
    approvalStatus: number;
  }) {
    return HttpHelper.postData(
      constants.apiUrl.ticketUrls.respondToApproval,
      object,
    );
  }

  static getCustomStatus(dataModel: any) {
    return HttpHelper.getData(
      constants.apiUrl.ticketUrls.getCustomStatus,
      null,
    );
  }
}
